import { defineStore } from 'pinia'
import { computed, reactive, ref } from 'vue'

import { capture } from '@/services/analytics'
import { ApiError, api } from '@/services/api'
import { useAuthStore } from '@/stores/auth'
import type { CreateGenerationRequest, GenerationListItem } from '@/types/api'

export const maxActiveGenerations = 3

export type GenerationPhase = 'uploading' | 'queued' | 'running' | 'succeeded' | 'failed'

export interface TrackedGeneration {
  id: string
  jobId: string
  family: 'image' | 'video'
  cost: number
  phase: GenerationPhase
  resultPath: string
  error: string
  errorStatus: number | null
  stalled: boolean
}

export interface GenerationToast {
  id: string
  generationId: string
  kind: 'ready' | 'failed' | 'stalled' | 'payment'
  message: string
}

export interface StartGeneration {
  family: 'image' | 'video'
  cost: number
  primary?: File
  secondary?: File
  request: (media: { primary?: string; secondary?: string }) => CreateGenerationRequest
}

const activePhases: GenerationPhase[] = ['uploading', 'queued', 'running']
export const pollIntervalMs = 3000
const maxPollDelayMs = 30_000
export const pollDeadlineMs = 60 * 60_000
const toastLifetimeMs = 10_000

export function isActivePhase(phase: GenerationPhase): boolean {
  return activePhases.includes(phase)
}

export function isPaymentError(record: TrackedGeneration): boolean {
  return record.errorStatus === 402 || (record.errorStatus === 403 && record.error === 'An active subscription is required')
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms))
}

function whenVisible(): Promise<void> {
  if (document.visibilityState !== 'hidden') return Promise.resolve()
  return new Promise((resolve) => {
    const onChange = () => {
      if (document.visibilityState === 'hidden') return
      document.removeEventListener('visibilitychange', onChange)
      resolve()
    }
    document.addEventListener('visibilitychange', onChange)
  })
}

export const useGenerationsStore = defineStore('generations', () => {
  const auth = useAuthStore()
  const items = ref<TrackedGeneration[]>([])
  const toasts = ref<GenerationToast[]>([])
  const composerId = ref('')
  const viewerId = ref('')
  let epoch = 0
  let videoSource: { id: string; file: File } | null = null

  const active = computed(() => items.value.filter((item) => isActivePhase(item.phase)))
  const activeCount = computed(() => active.value.length)
  const canStart = computed(() => activeCount.value < maxActiveGenerations)
  const uploading = computed(() => items.value.some((item) => item.phase === 'uploading'))
  const viewer = computed(() => find(viewerId.value))

  function find(id: string): TrackedGeneration | undefined {
    return id ? items.value.find((item) => item.id === id) : undefined
  }

  function byJobId(jobId: string): TrackedGeneration | undefined {
    return items.value.find((item) => item.jobId === jobId)
  }

  function alive(record: TrackedGeneration, session: number): boolean {
    return session === epoch && items.value.includes(record)
  }

  function track(record: TrackedGeneration): TrackedGeneration {
    const tracked = reactive(record) as TrackedGeneration
    items.value.push(tracked)
    return tracked
  }

  function focused(id: string): boolean {
    return id === composerId.value || id === viewerId.value
  }

  function toastFor(record: TrackedGeneration): Pick<GenerationToast, 'kind' | 'message'> {
    if (record.phase === 'succeeded') return { kind: 'ready', message: 'Your creation is ready' }
    if (record.stalled) return { kind: 'stalled', message: 'Still processing. Check Home later.' }
    if (isPaymentError(record)) return { kind: 'payment', message: record.error }
    if (record.jobId) return { kind: 'failed', message: 'Generation failed. Reserved credits were returned.' }
    return { kind: 'failed', message: record.error }
  }

  function notify(record: TrackedGeneration): void {
    if (focused(record.id)) return
    const toast: GenerationToast = { id: crypto.randomUUID(), generationId: record.id, ...toastFor(record) }
    toasts.value.push(toast)
    window.setTimeout(() => dismissToast(toast.id), toastLifetimeMs)
  }

  function settle(record: TrackedGeneration, phase: 'succeeded' | 'failed'): void {
    record.phase = phase
    void auth.refresh().catch(() => {})
    notify(record)
  }

  async function poll(record: TrackedGeneration, session = epoch): Promise<void> {
    record.stalled = false
    let failures = 0
    const deadline = Date.now() + pollDeadlineMs
    while (Date.now() < deadline) {
      await wait(Math.min(pollIntervalMs * 2 ** failures, maxPollDelayMs))
      await whenVisible()
      if (!alive(record, session)) return
      let job
      try {
        job = await api.generation(record.jobId)
        failures = 0
      } catch (error) {
        if (!alive(record, session)) return
        if (++failures < 5 && (!(error instanceof ApiError) || error.status >= 500 || error.status === 429)) continue
        record.stalled = true
        record.error = 'Could not check the generation. Please try again.'
        record.phase = 'failed'
        notify(record)
        return
      }
      if (!alive(record, session)) return
      if (job.status === 'queued' || job.status === 'running') {
        record.phase = job.status
        continue
      }
      if (job.status === 'succeeded' && job.result_url) {
        record.resultPath = job.result_url
        settle(record, 'succeeded')
        return
      }
      record.error = job.error ?? 'Generation failed. Reserved credits were returned.'
      settle(record, 'failed')
      return
    }
    if (!alive(record, session)) return
    record.stalled = true
    record.error = 'Your generation is still processing. Check again or find it in Home.'
    record.phase = 'failed'
    notify(record)
  }

  async function submit(record: TrackedGeneration, input: StartGeneration, session: number): Promise<void> {
    try {
      const [primary, secondary] = await Promise.all([
        input.primary ? api.uploadMedia(input.primary) : undefined,
        input.secondary ? api.uploadMedia(input.secondary) : undefined,
      ])
      if (!alive(record, session)) return
      const request = input.request({ primary: primary?.media_id, secondary: secondary?.media_id })
      const response = await api.createGeneration(request, crypto.randomUUID())
      capture('generation_started', {
        mode: request.mode, model: request.model, preset_id: request.preset_id ?? null, cost: response.cost,
      })
      if (!alive(record, session)) return
      record.jobId = response.job_id
      items.value
        .filter((item) => item !== record && item.jobId === record.jobId)
        .forEach((item) => remove(item.id))
      record.phase = response.status === 'running' ? 'running' : 'queued'
      void auth.refresh().catch(() => {})
      await poll(record, session)
    } catch (error) {
      if (!alive(record, session)) return
      record.errorStatus = error instanceof ApiError ? error.status : null
      record.error = error instanceof ApiError ? error.message : 'Generation could not be started.'
      record.phase = 'failed'
      notify(record)
    }
  }

  function start(input: StartGeneration): TrackedGeneration | null {
    if (!canStart.value) return null
    const record = track({
      id: crypto.randomUUID(),
      jobId: '',
      family: input.family,
      cost: input.cost,
      phase: 'uploading',
      resultPath: '',
      error: '',
      errorStatus: null,
      stalled: false,
    })
    void submit(record, input, epoch)
    return record
  }

  function adopt(item: GenerationListItem): void {
    if (item.status !== 'queued' && item.status !== 'running') return
    const existing = byJobId(item.job_id)
    if (existing && !existing.stalled) return
    if (existing) remove(existing.id)
    const record = track({
      id: crypto.randomUUID(),
      jobId: item.job_id,
      family: item.mode.endsWith('_video') ? 'video' : 'image',
      cost: item.cost,
      phase: item.status,
      resultPath: '',
      error: '',
      errorStatus: null,
      stalled: false,
    })
    void poll(record)
  }

  async function restore(): Promise<void> {
    const session = epoch
    const pages = await Promise.allSettled([api.generations('video'), api.generations('photo')])
    if (session !== epoch) return
    for (const page of pages) {
      if (page.status === 'fulfilled') page.value.items.forEach(adopt)
    }
  }

  function check(id: string): void {
    const record = find(id)
    if (!record?.stalled || !record.jobId || !canStart.value) return
    record.error = ''
    record.phase = 'queued'
    void poll(record)
  }

  function remove(id: string): void {
    items.value = items.value.filter((item) => item.id !== id)
    toasts.value = toasts.value.filter((toast) => toast.generationId !== id)
    if (composerId.value === id) composerId.value = ''
    if (viewerId.value === id) viewerId.value = ''
  }

  function dismissToast(id: string): void {
    const toast = toasts.value.find((item) => item.id === id)
    if (!toast) return
    toasts.value = toasts.value.filter((item) => item.id !== id)
    const record = find(toast.generationId)
    if (record && !isActivePhase(record.phase) && !focused(record.id)) remove(record.id)
  }

  function openViewer(id: string): void {
    if (!find(id)) return
    viewerId.value = id
    toasts.value = toasts.value.filter((toast) => toast.generationId !== id)
  }

  function closeViewer(): void {
    const record = viewer.value
    viewerId.value = ''
    if (record && !isActivePhase(record.phase) && !focused(record.id)) remove(record.id)
  }

  function offerVideoSource(file: File): string {
    const id = crypto.randomUUID()
    videoSource = { id, file }
    return id
  }

  function takeVideoSource(id: unknown): File | undefined {
    if (!videoSource || videoSource.id !== id) return undefined
    const { file } = videoSource
    videoSource = null
    return file
  }

  function reset(): void {
    epoch += 1
    videoSource = null
    items.value = []
    toasts.value = []
    composerId.value = ''
    viewerId.value = ''
  }

  return {
    items,
    toasts,
    composerId,
    viewerId,
    active,
    activeCount,
    canStart,
    uploading,
    viewer,
    find,
    byJobId,
    start,
    adopt,
    restore,
    check,
    remove,
    dismissToast,
    openViewer,
    closeViewer,
    offerVideoSource,
    takeVideoSource,
    reset,
  }
})
